import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api } from '../api/client';
import { settingsApi } from '../api/settings';
import {
  fallbackHintText,
  HINT_COPY_FALLBACK,
  HINT_GOT_IT,
  HINTS_OFF_NOTICE,
} from '../utils/hintCopy';

const HintsContext = createContext(null);

const MAX_HINT_FIELD_LEN = 600;

/** @param {unknown} value */
function sanitizeHintString(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_HINT_FIELD_LEN) return null;
  return trimmed;
}

/**
 * Accept only a plain object whose values are strings (known hint keys only).
 * Never throws; malformed payloads yield {} or partial safe entries.
 */
export function normalizeRemoteHints(payload) {
  try {
    let raw = payload;
    if (raw != null && typeof raw === 'object' && !Array.isArray(raw) && raw.data != null) {
      const inner = raw.data;
      raw = typeof inner === 'object' && inner != null && !Array.isArray(inner) ? inner : null;
    }
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
      return {};
    }
    const allowedKeys = new Set(Object.keys(HINT_COPY_FALLBACK));
    /** @type {Record<string, { title?: string, content?: string, actionLabel?: string }>} */
    const out = {};
    for (const [key, value] of Object.entries(raw)) {
      if (!allowedKeys.has(key)) continue;
      const asString = sanitizeHintString(value);
      if (asString) {
        out[key] = { content: asString };
      }
    }
    return out;
  } catch {
    return {};
  }
}

function resolveDefaultHintText(key) {
  const fallback = fallbackHintText(key);
  if (!fallback) return null;
  return {
    title: fallback.title,
    content: fallback.content,
    actionLabel: fallback.actionLabel || HINT_GOT_IT,
  };
}

const noop = () => {};

/** Safe fallback when HintsProvider is absent (tests and legacy renders). */
export const HINTS_DEFAULT_CONTEXT = {
  ready: false,
  hintsOn: false,
  hintsIntroSeen: true,
  setHintsIntroSeen: noop,
  toggleHints: noop,
  setHintsOn: noop,
  useHintText: resolveDefaultHintText,
  offNotice: null,
};

export function HintsProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [hintsOn, setHintsOn] = useState(true);
  const [hintsIntroSeen, setHintsIntroSeen] = useState(false);
  const [remoteHints, setRemoteHints] = useState({});
  const [offNotice, setOffNotice] = useState(null);
  const offNoticeTimerRef = useRef(null);

  const clearOffNoticeTimer = useCallback(() => {
    if (offNoticeTimerRef.current != null) {
      window.clearTimeout(offNoticeTimerRef.current);
      offNoticeTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearOffNoticeTimer(), [clearOffNoticeTimer]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await settingsApi.getPreferences();
        const prefs = res?.data ?? res ?? {};
        if (cancelled) return;
        if (typeof prefs.hints_on === 'boolean') setHintsOn(prefs.hints_on);
        if (typeof prefs.hints_intro_seen === 'boolean') {
          setHintsIntroSeen(prefs.hints_intro_seen);
        }
      } catch {
        /* default on */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/messages/hints');
        if (!cancelled) setRemoteHints(normalizeRemoteHints(res));
      } catch {
        /* bundled fallback only */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const persistHintsOn = useCallback(async (next) => {
    setHintsOn(next);
    try {
      await settingsApi.updatePreferences({ hints_on: next });
    } catch {
      /* UI state still updates; BE may not accept key yet */
    }
  }, []);

  const toggleHints = useCallback(() => {
    const next = !hintsOn;
    clearOffNoticeTimer();
    if (!next) {
      setOffNotice(HINTS_OFF_NOTICE);
      offNoticeTimerRef.current = window.setTimeout(() => {
        offNoticeTimerRef.current = null;
        setOffNotice(null);
      }, 4000);
    } else {
      setOffNotice(null);
    }
    persistHintsOn(next);
  }, [hintsOn, persistHintsOn, clearOffNoticeTimer]);

  const useHintText = useCallback((key) => {
    const remote = remoteHints[key];
    const fallback = fallbackHintText(key);
    if (remote && (remote.title || remote.content)) {
      return {
        title: remote.title || fallback?.title || '',
        content: remote.content || fallback?.content || '',
        actionLabel: remote.actionLabel || fallback?.actionLabel || HINT_GOT_IT,
      };
    }
    if (!fallback) return null;
    return {
      title: fallback.title,
      content: fallback.content,
      actionLabel: fallback.actionLabel || HINT_GOT_IT,
    };
  }, [remoteHints]);

  const value = useMemo(() => ({
    ready,
    hintsOn,
    hintsIntroSeen,
    setHintsIntroSeen,
    toggleHints,
    setHintsOn: persistHintsOn,
    useHintText,
    offNotice,
  }), [
    ready,
    hintsOn,
    hintsIntroSeen,
    toggleHints,
    persistHintsOn,
    useHintText,
    offNotice,
  ]);

  return (
    <HintsContext.Provider value={value}>
      {children}
      {offNotice ? (
        <p className="hints-off-notice" role="status">
          {offNotice}
        </p>
      ) : null}
    </HintsContext.Provider>
  );
}

export function useHints() {
  const ctx = useContext(HintsContext);
  return ctx ?? HINTS_DEFAULT_CONTEXT;
}

export function useHintText(key) {
  const { useHintText: resolve } = useHints();
  return useMemo(() => resolve(key), [resolve, key]);
}
